<template>
  <div ref="wrapperEl" data-growing-tiptap :class="wrapperClass" :style="wrapperStyle">
    <EditorContent
      class="growing-tiptap-input"
      :class="inputClass"
      :editor="editor"
      v-bind="$attrs"
    />
  </div>
</template>

<script setup>
  import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
  import { Editor, EditorContent } from "@tiptap/vue-3";
  import { Extension } from "@tiptap/core";
  import Document from "@tiptap/extension-document";
  import Paragraph from "@tiptap/extension-paragraph";
  import Text from "@tiptap/extension-text";
  import Bold from "@tiptap/extension-bold";
  import Italic from "@tiptap/extension-italic";
  import HardBreak from "@tiptap/extension-hard-break";
  import { BARE_PREFIX_PATTERN } from "./supplement-editor/utils/formatting";

  defineOptions({
    inheritAttrs: false,
  });

  const EnterAsHardBreak = Extension.create({
    name: "enterAsHardBreak",

    addKeyboardShortcuts() {
      return {
        Enter: ({ editor: ed }) => {
          ed.commands.setHardBreak();
          return true;
        },
      };
    },
  });

  const props = defineProps({
    modelValue: { type: String, default: "" },
    textBold: { type: Boolean, default: false },
    prefixBold: { type: Boolean, default: true },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    wrapperClass: { type: [String, Object, Array], default: "" },
    wrapperStyle: { type: [Object, String], default: null },
    inputClass: { type: String, default: "" },
    maxHeightPx: { type: Number, default: 0 },
  });

  const emit = defineEmits([
    "update:modelValue",
    "update:textBold",
    "update:prefixBold",
    "blur",
    "focus",
  ]);

  const wrapperEl = ref(null);
  const editor = ref(null);
  const isFocused = ref(false);
  const valueOnFocus = ref("");
  let resizeObserver = null;
  let isInitializing = true;

  function escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  function markdownToHtml(text) {
    if (!text) return "";
    return escapeHtml(text)
      .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
      .replace(/_(.+?)_/g, "<i>$1</i>");
  }

  function serializeToMarkdown(ed) {
    if (!ed) return "";

    let out = "";
    let active = { bold: false, italic: false };

    function closeMarks() {
      if (active.italic) out += "_";
      if (active.bold) out += "**";
      active = { bold: false, italic: false };
    }

    function openClose(next) {
      if (active.italic && !next.italic) out += "_";
      if (active.bold && !next.bold) out += "**";
      if (!active.bold && next.bold) out += "**";
      if (!active.italic && next.italic) out += "_";
      active = { ...next };
    }

    ed.state.doc.descendants((node) => {
      if (node.type.name === "paragraph") {
        return true;
      }

      if (node.type.name === "hardBreak") {
        closeMarks();
        out += "\n";
        return false;
      }

      if (node.isText) {
        const next = {
          bold: node.marks.some((m) => m.type.name === "bold"),
          italic: node.marks.some((m) => m.type.name === "italic"),
        };

        openClose(next);
        out += node.text;
      }

      return true;
    });

    closeMarks();

    return out.trimStart();
  }

  function buildDocContent(userMarkdown) {
    const lines = (userMarkdown || "").split("\n");
    const content = [];

    lines.forEach((line, index) => {
      if (index > 0) {
        content.push({ type: "hardBreak" });
      }

      if (line) {
        const html = markdownToHtml(line);
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        content.push(...parseHtmlToTiptapContent(tempDiv));
      }
    });

    return {
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: content.length ? content : undefined,
        },
      ],
    };
  }

  function parseHtmlToTiptapContent(container) {
    const result = [];
    const TEXT_NODE = 3;
    const ELEMENT_NODE = 1;

    function processNode(node, marks = []) {
      if (node.nodeType === TEXT_NODE) {
        const text = node.textContent;
        if (text) {
          result.push({
            type: "text",
            text,
            marks: marks.length ? marks : undefined,
          });
        }
      } else if (node.nodeType === ELEMENT_NODE) {
        let newMarks = [...marks];
        if (node.tagName === "B" || node.tagName === "STRONG") {
          if (!newMarks.some((m) => m.type === "bold")) {
            newMarks.push({ type: "bold" });
          }
        } else if (node.tagName === "I" || node.tagName === "EM") {
          if (!newMarks.some((m) => m.type === "italic")) {
            newMarks.push({ type: "italic" });
          }
        }
        for (const child of node.childNodes) {
          processNode(child, newMarks);
        }
      }
    }

    for (const child of container.childNodes) {
      processNode(child);
    }

    return result;
  }

  function getProseMirrorEl() {
    return wrapperEl.value?.querySelector(".ProseMirror") || null;
  }

  function autosize() {
    const el = getProseMirrorEl();
    if (!el) return;

    el.style.height = "auto";
    const h = el.scrollHeight;

    const clamped = props.maxHeightPx ? Math.min(h, props.maxHeightPx) : h;
    el.style.height = `${clamped}px`;
    el.style.overflowY = props.maxHeightPx && h > props.maxHeightPx ? "auto" : "hidden";
  }

  function setEditorContent(markdown) {
    const ed = editor.value;
    if (!ed) return;

    suppressBoldEmissions = true;
    const doc = buildDocContent(markdown);
    ed.commands.setContent(doc, false);
    syncBoldFromProps();
    suppressBoldEmissions = false;

    nextTick(() => {
      autosize();
    });
  }

  function findPrefixEndPosition(ed) {
    if (!ed) return null;

    const text = ed.state.doc.textContent;
    if (!text) return null;

    const leadingWhitespace = text.match(/^\s+/);
    const offset = leadingWhitespace ? leadingWhitespace[0].length : 0;
    const match = text.slice(offset).match(BARE_PREFIX_PATTERN);
    if (match) {
      return 1 + offset + match[0].length;
    }
    return null;
  }

  // Get the end position for bolding prefix, excluding trailing space
  // to prevent bold from merging with user content that starts with bold
  function getPrefixBoldEnd(ed, prefixEnd) {
    const text = ed.state.doc.textContent;
    const hasTrailingSpace = text[prefixEnd - 2] === " ";
    return hasTrailingSpace ? prefixEnd - 1 : prefixEnd;
  }

  function isPrefixBold(ed) {
    if (!ed) return false;

    const prefixEnd = findPrefixEndPosition(ed);
    if (!prefixEnd || prefixEnd <= 1) return false;
    const boldEnd = getPrefixBoldEnd(ed, prefixEnd);
    if (boldEnd <= 1) return false;

    let hasText = false;
    let allBold = true;

    ed.state.doc.nodesBetween(1, boldEnd, (node, pos) => {
      if (!node.isText || !node.text) return;

      // `nodesBetween` returns whole text nodes even when only part of the node
      // overlaps the checked range, so trim to the actual prefix overlap.
      const nodeStart = pos;
      const nodeEnd = pos + node.text.length;
      const overlapStart = Math.max(1, nodeStart);
      const overlapEnd = Math.min(boldEnd, nodeEnd);
      if (overlapStart >= overlapEnd) return;

      const overlapText = node.text.slice(overlapStart - nodeStart, overlapEnd - nodeStart);
      if (!overlapText.trim()) return;

      hasText = true;
      if (!node.marks.some((m) => m.type.name === "bold")) {
        allBold = false;
      }
    });

    return hasText && allBold;
  }

  function isTextAfterPrefixBold(ed) {
    if (!ed) return false;

    const prefixEnd = findPrefixEndPosition(ed);
    const startPos = prefixEnd || 1;
    const endPos = ed.state.doc.content.size - 1;

    if (startPos >= endPos) return false;

    let hasText = false;
    let allBold = true;

    ed.state.doc.nodesBetween(startPos, endPos, (node) => {
      if (node.isText && node.text.trim()) {
        hasText = true;
        if (!node.marks.some((m) => m.type.name === "bold")) {
          allBold = false;
        }
      }
    });

    return hasText && allBold;
  }

  function applyBoldAfterPrefix() {
    const ed = editor.value;
    if (!ed) return;

    const prefixEnd = findPrefixEndPosition(ed);
    const startPos = prefixEnd || 1;
    const endPos = ed.state.doc.content.size - 1;

    if (startPos >= endPos) return;

    applyBoldRange(ed, startPos, endPos);
  }

  function removeBoldAfterPrefix() {
    const ed = editor.value;
    if (!ed) return;

    const prefixEnd = findPrefixEndPosition(ed);
    const startPos = prefixEnd || 1;
    const endPos = ed.state.doc.content.size - 1;

    if (startPos >= endPos) return;

    removeBoldRange(ed, startPos, endPos);
  }

  function applyBoldToPrefix() {
    const ed = editor.value;
    if (!ed) return;

    const prefixEnd = findPrefixEndPosition(ed);
    if (!prefixEnd || prefixEnd <= 1) return;

    const boldEnd = getPrefixBoldEnd(ed, prefixEnd);
    applyBoldRange(ed, 1, boldEnd);
  }

  function removeBoldFromPrefix() {
    const ed = editor.value;
    if (!ed) return;

    const prefixEnd = findPrefixEndPosition(ed);
    if (!prefixEnd || prefixEnd <= 1) return;

    removeBoldRange(ed, 1, prefixEnd);
  }

  function applyBoldRange(ed, from, to) {
    if (!ed || from >= to) return;
    const boldMark = ed.state.schema.marks.bold;
    if (!boldMark) return;
    const tr = ed.state.tr.addMark(from, to, boldMark.create());
    ed.view.dispatch(tr);
  }

  function removeBoldRange(ed, from, to) {
    if (!ed || from >= to) return;
    const boldMark = ed.state.schema.marks.bold;
    if (!boldMark) return;
    const tr = ed.state.tr.removeMark(from, to, boldMark.create());
    ed.view.dispatch(tr);
  }

  function syncBoldFromProps() {
    const ed = editor.value;
    if (!ed) return;

    if (props.prefixBold) {
      applyBoldToPrefix();
    } else {
      removeBoldFromPrefix();
    }

    // Only APPLY bold when textBold is true.
    // Don't remove existing bold marks when textBold is false - this preserves
    // user-typed markdown formatting like **bold**.
    // The watch handler removes bold when textBold changes from true→false.
    if (props.textBold) {
      applyBoldAfterPrefix();
    }
  }

  let suppressBoldEmissions = false;
  let lastEmittedBoldState = null;
  let lastEmittedPrefixBoldState = null;

  onMounted(async () => {
    editor.value = new Editor({
      editable: !props.disabled,
      extensions: [
        Document,
        Paragraph,
        Text,
        Bold,
        Italic,
        HardBreak.configure({
          keepMarks: true,
        }),
        EnterAsHardBreak,
      ],
      content: buildDocContent(props.modelValue || ""),
      onUpdate: ({ editor: ed }) => {
        const markdown = serializeToMarkdown(ed);
        emit("update:modelValue", markdown);

        if (!suppressBoldEmissions && !isInitializing) {
          const prefixBoldState = isPrefixBold(ed);
          if (prefixBoldState !== lastEmittedPrefixBoldState) {
            lastEmittedPrefixBoldState = prefixBoldState;
            emit("update:prefixBold", prefixBoldState);
          }

          const textBold = isTextAfterPrefixBold(ed);
          if (textBold !== lastEmittedBoldState) {
            lastEmittedBoldState = textBold;
            emit("update:textBold", textBold);
          }
        }

        nextTick(() => autosize());
      },
      onFocus: ({ event }) => {
        isFocused.value = true;
        valueOnFocus.value = props.modelValue?.trimStart() ?? "";
        emit("focus", event);
      },
      onBlur: ({ event }) => {
        isFocused.value = false;
        const ed = editor.value;
        const rawText = ed.state.doc.textContent;
        const hasLeadingWhitespace = rawText !== rawText.trimStart();

        // Check for literal markdown syntax typed as plain text that should be converted
        const hasLiteralMarkdown = /\*\*.+?\*\*|_.+?_/.test(rawText);

        if (hasLeadingWhitespace || hasLiteralMarkdown) {
          const markdown = serializeToMarkdown(ed).trimStart();
          setEditorContent(markdown);
          emit("update:modelValue", markdown);
        }

        const markdown = serializeToMarkdown(ed);
        const changed = markdown !== valueOnFocus.value;
        emit("blur", event, { changed });
      },
      editorProps: {
        attributes: {
          "data-placeholder": props.placeholder || "",
          "role": "textbox",
          "aria-multiline": "true",
        },
      },
    });

    await nextTick();
    autosize();

    // Expose editor instance on DOM element for test access (e.g., Capybara)
    if (wrapperEl.value) {
      wrapperEl.value._tiptapEditor = editor.value;
    }

    const el = getProseMirrorEl();
    if (el) {
      resizeObserver = new ResizeObserver(() => autosize());
      resizeObserver.observe(el);
    }

    suppressBoldEmissions = true;
    syncBoldFromProps();
    suppressBoldEmissions = false;

    // Initialize tracking state AFTER applying initial bold
    lastEmittedBoldState = isTextAfterPrefixBold(editor.value);
    lastEmittedPrefixBoldState = isPrefixBold(editor.value);

    nextTick(() => {
      isInitializing = false;
    });
  });

  watch(
    () => props.disabled,
    (disabled) => {
      editor.value?.setEditable(!disabled);
    }
  );

  watch(
    () => props.textBold,
    (newVal, oldVal) => {
      if (!editor.value) return;
      if (newVal === oldVal) return;

      const currentState = isTextAfterPrefixBold(editor.value);
      if (newVal === currentState) return;

      // Don't manipulate selection while user is typing
      if (isFocused.value) return;

      suppressBoldEmissions = true;
      if (newVal) {
        applyBoldAfterPrefix();
      } else {
        removeBoldAfterPrefix();
      }
      lastEmittedBoldState = newVal;
      suppressBoldEmissions = false;
    }
  );

  watch(
    () => props.prefixBold,
    (newVal, oldVal) => {
      if (!editor.value) return;
      if (newVal === oldVal) return;

      // Don't manipulate selection while user is typing
      if (isFocused.value) return;

      suppressBoldEmissions = true;
      if (newVal) {
        applyBoldToPrefix();
      } else {
        removeBoldFromPrefix();
      }
      lastEmittedPrefixBoldState = newVal;
      suppressBoldEmissions = false;
    }
  );

  watch(
    () => props.modelValue,
    (next) => {
      if (isFocused.value) return;
      const ed = editor.value;
      if (!ed) return;

      const current = serializeToMarkdown(ed);
      const incoming = next ?? "";

      if (incoming !== current) {
        setEditorContent(incoming);
      }
    },
    { flush: "sync" }
  );

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    editor.value?.destroy();
  });

  function focus() {
    editor.value?.commands.focus("end");
  }

  defineExpose({ focus, editor });
</script>

<style lang="scss" scoped>
  div[data-growing-tiptap] {
    display: block;
  }

  .growing-tiptap-input {
    :deep(.ProseMirror) {
      white-space: pre-wrap;
      word-break: break-word;
      outline: none;
      min-height: 1.3em;
      box-sizing: border-box;

      p {
        margin: 0;
      }

      &:focus {
        outline: none;
      }

      &.ProseMirror-focused {
        outline: none;
      }
    }

    :deep(.ProseMirror p.is-empty:first-child::before) {
      content: attr(data-placeholder);
      float: left;
      opacity: 0.5;
      pointer-events: none;
      height: 0;
    }
  }
</style>
